import { forwardRef, type HTMLAttributes } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { STATUS_TONE_SURFACE } from './statusToneClasses.js';

const badge = tv({
  base: 'inline-flex items-center rounded-[var(--badge-radius,var(--radius-full))] font-semibold leading-none',
  variants: {
    tone: {
      neutral: '',
      primary: '',
      accent: '',
      info: '',
      success: '',
      warn: '',
      danger: '',
    },
    size: {
      sm: [
        'px-[var(--badge-padding-x-sm)]',
        'py-[var(--badge-padding-y-sm)]',
        'text-[length:var(--badge-font-size-sm)]',
      ].join(' '),
      md: [
        'px-[var(--badge-padding-x-md)]',
        'py-[var(--badge-padding-y-md)]',
        'text-[length:var(--badge-font-size-md)]',
      ].join(' '),
    },
    variant: {
      solid: '',
      outline: 'border bg-transparent',
    },
  },
  compoundVariants: [
    { tone: 'neutral', variant: 'solid', class: STATUS_TONE_SURFACE.neutral },
    { tone: 'neutral', variant: 'outline', class: 'border-[var(--color-an-border-hover)] text-[var(--color-an-text-muted)]' },
    {
      tone: 'primary',
      variant: 'solid',
      class:
        'bg-[var(--color-an-primary-soft)] text-[var(--color-an-primary-hover,var(--color-an-primary))]',
    },
    {
      tone: 'primary',
      variant: 'outline',
      class: 'border-[var(--color-an-primary)] text-[var(--color-an-primary)]',
    },
    {
      tone: 'accent',
      variant: 'solid',
      class: 'bg-[var(--color-an-accent-soft)] text-[var(--color-admin-accent,var(--color-an-primary))]',
    },
    {
      tone: 'accent',
      variant: 'outline',
      class: 'border-[var(--color-admin-accent)] text-[var(--color-admin-accent)]',
    },
    {
      tone: 'info',
      variant: 'solid',
      class: STATUS_TONE_SURFACE.info,
    },
    {
      tone: 'info',
      variant: 'outline',
      class: 'border-[var(--color-info,var(--color-an-info))] text-[var(--color-info,var(--color-an-info))]',
    },
    {
      tone: 'success',
      variant: 'solid',
      class: STATUS_TONE_SURFACE.success,
    },
    {
      tone: 'success',
      variant: 'outline',
      class: 'border-[var(--color-an-success)] text-[var(--color-an-success)]',
    },
    {
      tone: 'warn',
      variant: 'solid',
      class: STATUS_TONE_SURFACE.warn,
    },
    {
      tone: 'warn',
      variant: 'outline',
      class: 'border-[var(--color-an-warn)] text-[var(--color-an-warn)]',
    },
    {
      tone: 'danger',
      variant: 'solid',
      class: STATUS_TONE_SURFACE.danger,
    },
    {
      tone: 'danger',
      variant: 'outline',
      class: 'border-[var(--color-an-danger)] text-[var(--color-an-danger)]',
    },
  ],
  defaultVariants: { tone: 'neutral', size: 'md', variant: 'solid' },
});

type BadgeVariants = VariantProps<typeof badge>;

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & BadgeVariants;

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, size, variant, ...rest }, ref) => (
    <span ref={ref} className={badge({ tone, size, variant, className })} {...rest} />
  ),
);
Badge.displayName = 'Badge';
